import { useMemo } from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { GAS_METER } from "@volca/ui/components/ui/empty-machine";
import { EmptyPage } from "@volca/ui/components/ui/empty-page";
import { EmptyTableMessage } from "@volca/ui/components/ui/empty-table-message";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useBreakpoint } from "@volca/ui/hooks/use-breakpoint";
import { useReportHistory } from "@/profitability/query";
import type { EnrichedProfitabilityPeriod } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import {
  periodColumn,
  resultCol,
  statusCol,
  requestedCol,
  reportCol,
  reportRemoveCol,
  statusRemoveCol,
} from "./cols";

export const RunList = ({
  businessId,
  reportId,
  workflowVersion = 2,
  onOpenRun,
}: {
  businessId: string;
  reportId?: string;
  workflowVersion?: 1 | 2;
  /** A row opened: the run it stands for, and the row itself. */
  onOpenRun: (runId: string, period: EnrichedProfitabilityPeriod) => void;
}) => {
  const history = useReportHistory(businessId, reportId, workflowVersion);
  const breakpoint = useBreakpoint();
  const columns = useMemo(() => {
    if (breakpoint === "mobile")
      return [reportId ? reportRemoveCol : reportCol];
    // A template's runs are its periods; the business's list names a run by
    // its range, or by the documents it read.
    const period = periodColumn(
      workflowVersion === 1
        ? "Report period"
        : reportId
          ? "Period"
          : "Documents",
    );
    // A template's periods can be removed from it; the business's list is every template's.
    const status = reportId ? statusRemoveCol : statusCol;
    return breakpoint === "desktop"
      ? [period, resultCol, status, requestedCol]
      : [period, resultCol, status];
  }, [reportId, breakpoint, workflowVersion]);
  const runs = useMemo(
    () => history.data?.pages.flatMap((page) => page.items) ?? [],
    [history.data],
  );

  // Only when there is nothing to show. A failed poll or a failed refetch on
  // window focus leaves the loaded pages in the cache, and replacing a working
  // list with an error panel over one bad request is worse than showing it.
  if (history.error && !history.data)
    return (
      <EmptyTableMessage>
        <Stack gap={4} align="center">
          <Text tone="muted">{reportError(history.error)}</Text>
          <Button variant="secondary" onClick={() => void history.refetch()}>
            Try again
          </Button>
        </Stack>
      </EmptyTableMessage>
    );

  // The period picker lives in the page header, so the empty state points at it
  // rather than repeating the action as a second button.
  if (!history.isPending && !runs.length && !reportId)
    return (
      <EmptyPage
        bleed
        art={GAS_METER}
        title="No reports yet"
        tagline={
          workflowVersion === 1
            ? "Choose a period for your first report"
            : "Add files for your first report"
        }
        footnote={
          workflowVersion === 1
            ? "Select a report period above, then choose Run report. Your completed reports will appear here."
            : "Add your documents above. Your completed reports will appear here."
        }
        className="min-h-0"
      />
    );
  if (!history.isPending && !runs.length)
    return (
      <MachineEmptyState
        art={GAS_METER}
        layout="workspace"
        title="No runs yet"
        description="Use Upload files to add this period's documents."
        footnote="Each run and its results will appear here."
      />
    );

  return (
    <Stack className="w-full" align="start" gap={4}>
      <Card className="w-full gap-0 overflow-hidden border-line py-0 shadow-none">
        <SimpleTable
          label={reportId ? "Agent runs" : "Job costing reports"}
          data={runs}
          columns={columns}
          isLoading={history.isPending}
          getRowId={(run) =>
            run.start_date && run.end_date
              ? `${run.start_date}/${run.end_date}`
              : `run/${run.id}`
          }
          onRowClick={(run) => onOpenRun(run.id, run)}
          className="w-full [&_td:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:first-child]:pl-6 [&_th:last-child]:pr-6"
          scrollAreaClassName="pr-0"
        />
      </Card>
      {history.hasNextPage && (
        <Stack
          className="w-full"
          align="start"
          gap={4}
          direction="row"
          justify="center"
        >
          <Button
            disabled={history.isFetchingNextPage}
            variant="secondary"
            aria-busy={history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
          >
            Load more
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
